import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguageUrl } from '../utils/languageUtils';

const PrivacyBanner = () => {
  const { t } = useTranslation();
  const { getLanguageUrl } = useLanguageUrl();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: true,
    functional: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load preferences from localStorage
      try {
        const consent = JSON.parse(consentGiven);
        setPreferences({
          essential: true, // Always true
          analytics: typeof consent.analytics === 'boolean' ? consent.analytics : true,
          functional: typeof consent.functional === 'boolean' ? consent.functional : true
        });
      } catch (e) {
        // If parsing fails, fallback to defaults
        setPreferences({
          essential: true,
          analytics: true,
          functional: true
        });
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    
    // Initialize analytics if accepted
    if (window.gtag && consent.analytics) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleDeclineAll = () => {
    const consent = {
      essential: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    
    // Deny analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
    
    // Update analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied'
      });
    }
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300" />

      {/* Cookie Consent Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t-4 border-blue-600 shadow-2xl animate-slide-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {!showSettings ? (
            // Main Banner View
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {t('cookieBanner.title')}
                    </h3>
                    <p className="text-sm text-slate-700 mb-3">
                      {t('cookieBanner.description')}
                      {' '}
                      <Link 
                        to={getLanguageUrl('/cookies')}
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                      >
                        {t('cookieBanner.learnMore')}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {t('cookieBanner.customize')}
                </button>
                <button
                  onClick={handleDeclineAll}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {t('cookieBanner.declineAll')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg shadow-blue-600/30"
                >
                  {t('cookieBanner.acceptAll')}
                </button>
              </div>
            </div>
          ) : (
            // Settings View
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">
                  {t('cookieBanner.settings.title')}
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-slate-500 hover:text-slate-700 focus:outline-none"
                  aria-label="Close settings"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-slate-600">
                {t('cookieBanner.settings.description')}
              </p>

              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-900">
                        {t('cookieBanner.settings.essential.title')}
                      </h4>
                      <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
                        {t('cookieBanner.settings.required')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {t('cookieBanner.settings.essential.description')}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-6 bg-blue-600 rounded-full relative cursor-not-allowed opacity-50">
                      <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-slate-900">
                      {t('cookieBanner.settings.analytics.title')}
                    </h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {t('cookieBanner.settings.analytics.description')}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        preferences.analytics ? 'bg-blue-600' : 'bg-slate-300'
                      }`}
                      aria-label="Toggle analytics cookies"
                    >
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200 ${
                        preferences.analytics ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </button>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-slate-900">
                      {t('cookieBanner.settings.functional.title')}
                    </h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {t('cookieBanner.settings.functional.description')}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        preferences.functional ? 'bg-blue-600' : 'bg-slate-300'
                      }`}
                      aria-label="Toggle functional cookies"
                    >
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200 ${
                        preferences.functional ? 'right-0.5' : 'left-0.5'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {t('cookieBanner.settings.cancel')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg shadow-blue-600/30"
                >
                  {t('cookieBanner.settings.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PrivacyBanner;
